import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import InfoBubble from "../components/InfoBubble";
import SupportChoiceBar from "../components/SupportChoiceBar";
import useGuidance from "../hooks/useGuidance";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2, Plus, Users, MapPin, ShieldCheck, Activity,
  Search, Filter, ChevronRight, Loader2, Heart, AlertTriangle
} from "lucide-react";
import {
  ORG_STATUS_STYLES as STATUS_STYLES,
  ORG_TYPE_LABELS as TYPE_LABELS,
  RISK_STYLES,
  getHealthColor,
} from "../constants/registry";

const HealthBar = ({ score }) => {
  const color = getHealthColor(score);
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono" style={{ color }}>{score}</span>
    </div>
  );
};

const CRMOrganizationsPage = () => {
  const { isAdmin, isAuditor } = useAuth();
  const navigate = useNavigate();
  const { guidance } = useGuidance();
  const { t } = useTranslation('platform');
  const [orgs, setOrgs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", type: "client", regions: [], risk_category: "medium", industry: "" });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      const [orgsRes, summaryRes] = await Promise.all([
        fetch(`${API}/crm/organizations?${params}`, { credentials: "include" }),
        fetch(`${API}/crm/health-summary`, { credentials: "include" }),
      ]);
      if (orgsRes.ok) setOrgs(await orgsRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (err) {
      console.error("CRM fetch failed:", err);
    }
    setLoading(false);
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/crm/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Organization created");
        setShowCreate(false);
        setForm({ name: "", type: "client", regions: [], risk_category: "medium", industry: "" });
        fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to create organization");
      }
    } catch (err) {
      toast.error("Failed to create organization");
    }
    setCreating(false);
  };

  const toggleRegion = (r) => {
    setForm(prev => ({
      ...prev,
      regions: prev.regions.includes(r) ? prev.regions.filter(x => x !== r) : [...prev.regions, r]
    }));
  };

  const filtered = orgs.filter(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (!isAdmin && !isAuditor) {
    return <QMSLayout title={t('crm.organizations')}><div className="text-center py-12 text-[#8B95A5]">Access restricted to admin and auditor roles.</div></QMSLayout>;
  }

  return (
    <QMSLayout title={t('crm.organizations')}>
      <div className="max-w-6xl mx-auto" data-testid="crm-organizations-page">
        {guidance?.info_hint && <InfoBubble text={guidance.info_hint} title={t('crm.organizations')} />}
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="crm-summary">
            {[
              { label: t('crm.totalOrgs'), value: summary.total_organizations, icon: Building2, color: "#36A9E1" },
              { label: t('crm.activeOrgs'), value: summary.by_status?.active || 0, icon: Activity, color: "#10B981" },
              { label: "Onboarding", value: summary.by_status?.onboarding || 0, icon: ShieldCheck, color: "#3B82F6" },
              { label: t('crm.contacts'), value: summary.total_contacts, icon: Users, color: "#A78BFA" },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="p-4 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27]">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} style={{ color }} />
                  <span className="text-[#8B95A5] text-xs">{label}</span>
                </div>
                <span className="text-white text-xl font-bold font-mono">{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" />
            <input
              type="text"
              placeholder={t('crm.searchOrgs')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
              data-testid="crm-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm"
            data-testid="crm-status-filter"
          >
            <option value="all">{t('crm.allStatuses')}</option>
            <option value="active">Active</option>
            <option value="onboarding">Onboarding</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm"
            data-testid="crm-type-filter"
          >
            <option value="all">{t('crm.allTypes')}</option>
            <option value="client">Client</option>
            <option value="provider">Provider</option>
            <option value="internal">Internal</option>
          </select>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] transition-colors"
            data-testid="crm-create-btn"
          >
            <Plus size={14} /> {t('crm.newOrganization')}
          </button>
        </div>

        {/* Org List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#8B95A5]">
            <Building2 size={32} className="mx-auto mb-2 opacity-40" />
            <p>{t('crm.noOrgs')}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="crm-org-list">
            {filtered.map(org => {
              const st = STATUS_STYLES[org.status] || STATUS_STYLES.inactive;
              const risk = RISK_STYLES[org.risk_category] || RISK_STYLES.medium;
              return (
                <button
                  key={org.org_id}
                  onClick={() => navigate(`/crm/organizations/${org.org_id}`)}
                  className="w-full text-left p-4 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] hover:border-[rgba(255,255,255,0.15)] transition-colors group"
                  data-testid={`crm-org-${org.org_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[rgba(54,169,225,0.1)] flex items-center justify-center shrink-0">
                      <Building2 size={18} className="text-[#36A9E1]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold text-sm truncate">{org.name}</h3>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                        <span className="text-[#8B95A5] text-xs">{TYPE_LABELS[org.type] || org.type}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#8B95A5]">
                        {org.regions?.length > 0 && (
                          <span className="flex items-center gap-1"><MapPin size={10} />{org.regions.join(", ")}</span>
                        )}
                        <span className="flex items-center gap-1"><Users size={10} />{org.contact_count || 0} contacts</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${risk.bg} ${risk.text}`}>{org.risk_category} risk</span>
                        {org.industry && <span>{org.industry}</span>}
                      </div>
                    </div>
                    <HealthBar score={org.health_score || 50} />
                    <ChevronRight size={16} className="text-[#8B95A5] group-hover:text-white transition-colors shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="crm-create-modal">
            <form onSubmit={handleCreate} className="bg-[#1A1D27] border border-[rgba(255,255,255,0.1)] rounded-lg p-6 w-full max-w-lg shadow-2xl">
              <h2 className="text-white font-semibold text-lg mb-4 font-['Syne']">{t('crm.newOrganization')}</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">{t('crm.orgName')} *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                    required
                    data-testid="crm-form-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">{t('crm.orgType')}</label>
                    <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm" data-testid="crm-form-type">
                      <option value="client">Client</option>
                      <option value="provider">Provider</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">{t('crm.riskCategory')}</label>
                    <select value={form.risk_category} onChange={e => setForm(p => ({ ...p, risk_category: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm" data-testid="crm-form-risk">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">{t('crm.industry')}</label>
                  <input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none" data-testid="crm-form-industry" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">{t('crm.regions')}</label>
                  <div className="flex flex-wrap gap-1.5">
                    {["US", "EU", "CA", "BR", "LATAM", "AU", "JP", "CN", "UK", "OUS"].map(r => (
                      <button type="button" key={r} onClick={() => toggleRegion(r)} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${form.regions.includes(r) ? "bg-[#36A9E1] text-white" : "bg-[rgba(255,255,255,0.04)] text-[#8B95A5] hover:text-white"}`} data-testid={`crm-form-region-${r}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-md text-sm text-[#8B95A5] hover:text-white transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] disabled:opacity-50 transition-colors" data-testid="crm-form-submit">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {t('crm.createOrg')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default CRMOrganizationsPage;
